import { NodeProps, useUpdateNodeInternals } from '@xyflow/react';
import DraggableCalculationFunctionItem from './DraggableCalculationFunctionItem';
import CalculationRightPanel from '../CalculationControlPanel/CalculationControlPanel';
import { Position, Handle } from '@xyflow/react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import {
  adjustSourceLength,
  getFlowTypeFromFunctionName,
  isFlowUsedInOtherFlowsAsSource,
} from '../utils';
import { minMax } from '../constants';
import { TWindowFn } from '../../../types/types';
import { useDispatch } from 'react-redux';
import { setActiveFlow } from '../../../redux/Calculations/CalculationsActions';
import { useEffect } from 'react';
import { flowNode, flowNodeData } from './CalculationCanvas';

export default function CalcFlowNode({ id, data }: NodeProps<flowNode>) {
  const { flowUID, propKey, indexKey, parentRect, deleteFlow, setFlowDisplayNames }: flowNodeData =
    data;
  const dispatch = useDispatch();
  const updateNodeInternals = useUpdateNodeInternals();
  // if (!flowUID || !propKey) return null; commented becouse creating issue on delete
  const { flows, currentCalculationSession } = useSelector((state: RootState) => {
    const flows =
      state.calculations.properties[propKey]?.currentCalculationSession?.calculationInfo.flows ??
      {};
    return {
      flows,

      currentCalculationSession:
        state.calculations.properties[propKey]?.currentCalculationSession ?? null,
    };
  });
  const isInvalid =
    !flowUID || !propKey || !flows[flowUID] || !flows[flowUID][0] || !currentCalculationSession;

  useEffect(() => {
    if (!isInvalid) {
      updateNodeInternals(flowUID);
    }
  }, [isInvalid, flowUID]);

  if (isInvalid) return null;

  const activeFlow = currentCalculationSession?.activeFlow ?? null;
  const isActive = activeFlow === flowUID;
  const flowName: string = flows[flowUID][0].flow;
  const flowType = getFlowTypeFromFunctionName(flowName);
  if (!flowType) return null;

  const resultantType =
    flowName === 'IfElse'
      ? currentCalculationSession?.calculationInfo.selectedResultType[flowUID]
      : currentCalculationSession?.calculationInfo.flows[flowUID][0].resultantDatatype;

  const minimumSourcesForThisFlow = minMax[flowType][flowName]?.min;

  const sourceTypes = currentCalculationSession?.calculationInfo.flows[flowUID][0].sourceType ?? [];

  const windowFn: TWindowFn | null =
    currentCalculationSession?.calculationInfo.flows[flowUID][0].flowOverride?.windowFunction ??
    null;
  const lengthOfSourcesForThisFlow =
    adjustSourceLength(flowUID, flowName, currentCalculationSession) ?? 0;
  const aggregationType = flows[flowUID][0].isAggregation;
  const flowDisplayName = currentCalculationSession?.flowDisplayNames[flowUID] ?? '';
  const doesSourceArrayContainFieldOrFlow =
    sourceTypes?.length > 0 && flowName === 'concat'
      ? sourceTypes?.includes('field') ||
        sourceTypes?.includes('flow') ||
        sourceTypes?.includes('text')
      : sourceTypes?.includes('field') || sourceTypes?.includes('flow');

  const isFlowUsedInOtherFlows = isFlowUsedInOtherFlowsAsSource(flowUID, currentCalculationSession);

  const _setActiveFlow = (propKey: string, calculationFlowUID: string | null) =>
    dispatch(setActiveFlow(propKey, calculationFlowUID));

  return (
    <div
      className="flow-nodes"
      style={{
        position: 'relative',
        width: '300px',
        transition: '0.15s ease',
        minWidth: `250px`,
        maxWidth: '300px',
        borderRadius: '8px',
        border: activeFlow === flowUID ? '2px solid #2BB9BB' : '2px solid #ccc',
        color: ' #5d5c5c',
        textAlign: 'start',
        backgroundColor: 'white',
      }}
    >
      <div className={activeFlow === flowUID ? 'calculation_flow' : ''}>
        <DraggableCalculationFunctionItem
          activeFlowName={flowName}
          doesSourceArrayContainFieldOrFlow={doesSourceArrayContainFieldOrFlow}
          parentWidth={parentRect.width}
          isFlowUsedInOtherFlowsAsSource={isFlowUsedInOtherFlows}
          remainingSources={
            minimumSourcesForThisFlow - lengthOfSourcesForThisFlow < 1
              ? 0
              : minimumSourcesForThisFlow - lengthOfSourcesForThisFlow
          }
          totalSources={minimumSourcesForThisFlow}
          indexKey={indexKey}
          activeFlow={activeFlow}
          flowUID={flowUID}
          _flows={flows}
          propKey={propKey}
          resultantType={resultantType}
          aggregationType={aggregationType}
          windowFn={windowFn ?? undefined}
          deleteFlow={deleteFlow}
          setActiveFlow={_setActiveFlow}
          setFlowDisplayNames={setFlowDisplayNames}
          flowDisplayName={flowDisplayName}
        />
      </div>
      <div
        className="nodrag nowheel"
        data-nodrag
        data-no-drag
        data-prevent-drag-handles
        style={{
          maxHeight: isActive ? 520 : 0,
          opacity: isActive ? 1 : 0,
          transform: isActive ? 'translateY(0)' : 'translateY(-4px)',
          paddingTop: isActive ? 8 : 0,
          paddingBottom: isActive ? 8 : 0,
          overflow: 'hidden',
          transition: `
    max-height 100ms ease,
    opacity 100ms ease,
    transform 100ms ease,
    padding 100ms ease
  `,
        }}
      >
        <CalculationRightPanel />
      </div>
      <Handle
        style={{
          opacity: 0,
          width: 0,
          height: 0,
          pointerEvents: 'none',
        }}
        type="source"
        id="left"
        position={Position.Left}
      />
      <Handle
        style={{
          opacity: 0,
          width: 0,
          height: 0,
          pointerEvents: 'none',
        }}
        type="source"
        id="right"
        position={Position.Right}
      />

      <Handle
        style={{
          opacity: 0,
          width: 0,
          height: 0,
          pointerEvents: 'none',
        }}
        type="target"
        id="left"
        position={Position.Left}
      />
      <Handle
        style={{
          opacity: 0,
          width: 0,
          height: 0,
          pointerEvents: 'none',
        }}
        type="target"
        id="right"
        position={Position.Right}
      />
      <Handle
        style={{
          opacity: 0,
          width: 0,
          height: 0,
          pointerEvents: 'none',
        }}
        type="source"
        id="top"
        position={Position.Top}
      />
      <Handle
        style={{
          opacity: 0,
          width: 0,
          height: 0,
          pointerEvents: 'none',
        }}
        type="source"
        id="bottom"
        position={Position.Bottom}
      />

      <Handle
        style={{
          opacity: 0,
          width: 0,
          height: 0,
          pointerEvents: 'none',
        }}
        type="target"
        id="top"
        position={Position.Top}
      />
      <Handle
        style={{
          opacity: 0,
          width: 0,
          height: 0,
          pointerEvents: 'none',
        }}
        type="target"
        id="bottom"
        position={Position.Bottom}
      />
    </div>
  );
}
